import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usePurchaseBills, useCreatePurchaseBill } from '../hooks/useTransactions';
import { Card, CardHeader, CardBody } from '@/shared/components/ui/Card';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { useAccountsVendors } from '../hooks/useParties';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, getApiErrorMessage } from '@/shared/api/axios';
import toast from 'react-hot-toast';

export function PurchaseBillList() {
  const { data: bills, isLoading } = usePurchaseBills();
  const { data: vendors, isLoading: vendorsLoading } = useAccountsVendors();
  const createBill = useCreatePurchaseBill();
  const queryClient = useQueryClient();
  const [editingBill, setEditingBill] = useState<any | null>(null);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['accounts', 'purchase-bills'] });
  const saveEdit = useMutation({ mutationFn: async (payload: any) => (await api.put(`/api/accounts/purchase-bills/${editingBill._id}`, payload)).data, onSuccess: () => { refresh(); setIsCreating(false); setEditingBill(null); toast.success('Bill updated.'); }, onError: (error) => toast.error(getApiErrorMessage(error, 'Could not update bill.')) });
  const billAction = useMutation({ mutationFn: async ({ id, action, reason }: { id: string; action: 'finalize' | 'reverse' | 'delete'; reason?: string }) => action === 'delete' ? api.delete(`/api/accounts/purchase-bills/${id}`) : api.post(`/api/accounts/purchase-bills/${id}/${action}`, { reason }, { headers: { 'X-Idempotency-Key': crypto.randomUUID() } }), onSuccess: (_, values) => { refresh(); toast.success(values.action === 'finalize' ? 'Bill finalized and posted.' : values.action === 'reverse' ? 'Bill reversed.' : 'Draft deleted.'); }, onError: (error) => toast.error(getApiErrorMessage(error, 'Bill action failed.')) });
  
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().slice(0,10),
    dueDate: new Date().toISOString().slice(0,10),
    vendorId: '',
    vendorBillNumber: '',
    description: '',
    amount: 0,
    poId: '',
  });

  // Optional PO link for a bill entered directly here rather than via the
  // Purchase Orders tab's "To Bill" conversion (which already sets this).
  // Until the backend accepted poId at all, a bill created this way had no
  // way to ever be traced back to the order it was for.
  const [openPos, setOpenPos] = useState<any[]>([]);
  useEffect(() => {
    api.get('/api/accounts/commercial-documents/purchase-orders')
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        setOpenPos(list.filter((po: any) => ['issued', 'partially_billed'].includes(po.status)));
      })
      .catch(() => {});
  }, []);

  const openEdit = (bill: any) => { const line = bill.lineItems?.[0] || {}; setEditingBill(bill); setFormData({ date: String(bill.date).slice(0, 10), dueDate: String(bill.dueDate).slice(0, 10), vendorId: typeof bill.vendorId === 'string' ? bill.vendorId : bill.vendorId?._id || '', vendorBillNumber: bill.vendorBillNumber || '', description: line.description || '', amount: Number(line.rate || line.amount || bill.grandTotal), poId: '' }); setIsCreating(true); };

  // Deep link from the Ledger Explorer (?billId=) straight into this bill's
  // detail/edit view — the closest thing this screen has to a read-only
  // detail page — instead of leaving a ledger row as a dead end.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const billId = searchParams.get('billId');
    if (!billId || !bills) return;
    const found = bills.find((b: any) => b._id === billId);
    if (found) openEdit(found);
    const next = new URLSearchParams(searchParams);
    next.delete('billId');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, bills]);

  if (isLoading) return <Skeleton className="h-[400px]" />;

  const formatCurrency = (value: unknown) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 })
      .format(Number(value) || 0);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      vendorId: formData.vendorId,
      date: formData.date,
      dueDate: formData.dueDate,
      vendorBillNumber: formData.vendorBillNumber,
      lineItems: [
        { description: formData.description, quantity: 1, rate: Number(formData.amount), discount: 0 }
      ],
      ...(formData.poId ? { poId: formData.poId } : {}),
    };
    if (editingBill) await saveEdit.mutateAsync(payload); else await createBill.mutateAsync(payload);
    setIsCreating(false);
    setFormData({
      date: new Date().toISOString().slice(0,10), dueDate: new Date().toISOString().slice(0,10),
      vendorId: '', vendorBillNumber: '', description: '', amount: 0, poId: '',
    });
    setEditingBill(null);
  };

  const vendorOptions = (vendors || []).map(vendor => ({
    value: vendor._id,
    label: vendor.name || vendor.contactId?.displayName || vendor.contactId?.name || 'Unnamed vendor',
  }));

  return (
    <>
      <Card className="shadow-xs h-full flex flex-col">
        <CardHeader 
          className="flex-none" 
          title="Purchase Bills" 
          action={
            <Button size="sm" onClick={() => { setEditingBill(null); setIsCreating(true); }}>
              + New Bill
            </Button>
          } 
        />
        <CardBody className="flex-1 overflow-auto p-0">
          <div className="border rounded-md">
            <div className="grid grid-cols-8 bg-muted/50 py-2 px-4 border-b text-xs font-medium text-muted-foreground uppercase tracking-wider">
              <div>Date</div>
              <div>Bill #</div>
              <div className="col-span-2">Vendor</div>
              <div>Amount</div>
              <div>Status</div><div className="col-span-2 text-right">Actions</div>
            </div>
            <div className="divide-y">
              {bills?.map((bill: any) => (
                <div key={bill._id} className="grid grid-cols-8 py-3 px-4 text-sm hover:bg-muted/30">
                  <div className="text-muted-foreground">{new Date(bill.date).toLocaleDateString('en-IN')}</div>
                  <div className="font-medium text-foreground">{bill.billNumber || bill.vendorBillNumber || '—'}</div>
                  <div className="col-span-2 truncate">
                    {bill.vendorId?.name || bill.vendorName || 'Vendor not assigned'}
                  </div>
                  <div className="font-mono">{formatCurrency(bill.grandTotal ?? bill.netTotal ?? bill.totalAmount)}</div>
                  <div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      bill.status === 'draft' ? 'bg-secondary text-secondary-foreground' :
                      bill.status === 'received' ? 'bg-blue-100 text-blue-700' :
                      'bg-green-100 text-green-700'
                    }`}>
                      {bill.status}
                    </span>
                  </div>
                  <div className="col-span-2 text-right">{bill.status === 'draft' && <><Button size="sm" variant="ghost" onClick={() => openEdit(bill)}>Edit</Button><Button size="sm" variant="ghost" onClick={() => billAction.mutate({ id: bill._id, action: 'finalize' })}>Finalize</Button><Button size="sm" variant="ghost" className="text-destructive" onClick={() => window.confirm('Delete this draft bill?') && billAction.mutate({ id: bill._id, action: 'delete' })}>Delete</Button></>}{['unpaid', 'partially_paid', 'paid', 'overdue'].includes(bill.status) && <Button size="sm" variant="ghost" onClick={() => { const reason = window.prompt('Reason for reversal'); if (reason) billAction.mutate({ id: bill._id, action: 'reverse', reason }); }}>Reverse</Button>}</div>
                </div>
              ))}
              {bills?.length === 0 && (
                <div className="p-8 text-center text-muted-foreground">
                  No Purchase Bills found.
                </div>
              )}
            </div>
          </div>
        </CardBody>
      </Card>

      <Modal open={isCreating} onClose={() => { setIsCreating(false); setEditingBill(null); }} title={editingBill ? 'Edit Purchase Bill' : 'Create Purchase Bill'}>
        <form onSubmit={handleCreate} className="space-y-4 pt-4">
          <div className="space-y-2">
            <Select
              label="Vendor"
              required
              value={formData.vendorId}
              onChange={e => setFormData({ ...formData, vendorId: e.target.value })}
              options={vendorOptions}
              placeholder={vendorsLoading ? 'Loading vendors...' : 'Select vendor'}
            />
            {!vendorOptions.length && !vendorsLoading && <p className="text-xs text-amber-700">Add a vendor from Customers & Vendors first.</p>}
          </div>
          {!editingBill && (
            <div className="space-y-2">
              <Select
                label="Purchase Order (optional)"
                value={formData.poId}
                onChange={e => setFormData({ ...formData, poId: e.target.value })}
                options={openPos.map((po: any) => ({ value: po._id, label: `${po.documentNumber} — ${formatCurrency(po.grandTotal)}` }))}
                placeholder="No linked purchase order"
              />
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Bill Date</label>
              <Input type="date" required value={formData.date} onChange={e => setFormData({ ...formData, date: e.target.value })} />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Payment Due Date</label>
              <Input type="date" required value={formData.dueDate} onChange={e => setFormData({ ...formData, dueDate: e.target.value })} />
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Vendor Bill Number</label>
            <Input value={formData.vendorBillNumber} onChange={e => setFormData({ ...formData, vendorBillNumber: e.target.value })} placeholder="e.g. INV-4582" />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">What was purchased?</label>
            <Input required value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} placeholder="e.g. Office supplies" />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Total Amount (₹)</label>
            <Input type="number" required value={formData.amount} onChange={e => setFormData({ ...formData, amount: Number(e.target.value) })} />
          </div>
          <p className="text-xs text-muted-foreground">A simple one-line draft bill will be created. You can review it before posting.</p>

          <div className="flex justify-end space-x-2 pt-4">
            <Button type="button" variant="outline" onClick={() => { setIsCreating(false); setEditingBill(null); }}>Cancel</Button>
            <Button type="submit" disabled={createBill.isPending || saveEdit.isPending || !formData.vendorId || !formData.description || formData.amount <= 0}>{editingBill ? 'Save Changes' : 'Save Draft Bill'}</Button>
          </div>
        </form>
      </Modal>
    </>
  );
}
